import type { AnalyticInsertSupa } from "@shared/supabase.types.ts";
import useSWR from "swr";
import { z } from "zod";
import { SessionFeedbackDataSchema } from "@/components/tutor/event-feedback-form";
import { supabaseClient } from "@/lib/supabase";
import { ANALYTICS_CACHE_KEYS } from "./cache-keys";

const SessionFeedbackDataIdSchema = SessionFeedbackDataSchema.extend({
  analytic_id: z.number(),
  created_at: z.string(),
});

export type SessionFeedbackDataId = z.infer<typeof SessionFeedbackDataIdSchema>;

export async function createAnalytic(input: AnalyticInsertSupa) {
  const res = await supabaseClient.from("analytics").insert(input);
  return res.statusText === "Created";
}

export async function fetchFeedback() {
  const { data, error } = await supabaseClient
    .from("analytics")
    .select("*")
    .eq("class", "FEEDBACK")
    .order("created_at", { ascending: false });

  debugger;

  if (error) {
    throw error;
  }

  const parsedData: SessionFeedbackDataId[] = [];
  for (const item of data) {
    const result = SessionFeedbackDataSchema.safeParse(item.event);
    if (result.success) {
      parsedData.push({
        ...result.data,
        analytic_id: item.analytic_id,
        created_at: item.created_at || new Date().toISOString(),
      });
    }
  }

  return parsedData;
}

export function useFeedback() {
  const {
    data = [],
    isLoading,
    error,
    mutate,
  } = useSWR(ANALYTICS_CACHE_KEYS.FEEDBACK, fetchFeedback);

  return {
    feedback: data,
    isLoading,
    error,
    mutate,
  };
}

// Interface for notification analytics data
export interface NotificationAnalytic {
  analytic_id: number;
  class: string;
  event: {
    event: string;
    recipient_email?: string;
    recipient_phone?: string;
    subject?: string;
    message?: string;
    event_id?: number;
    transaction_id?: number;
    errorMessage?: string;
  };
  created_at: string;
}

// Type guard to check if the event object is a notification event
function isNotificationEvent(event: unknown): event is NotificationAnalytic["event"] {
  if (!event || typeof event !== "object") {
    return false;
  }
  const e = event as Record<string, unknown>;
  return (
    typeof e.event === "string" &&
    (e.event === "Event Reminder Sent" ||
      e.event === "Payment Reminder Sent" ||
      e.event === "Admin Event Reminder Report Sent" ||
      e.event === "Admin Payment Reminder Report Sent")
  );
}

/**
 * Fetch notification and payment analytics for a specific user
 * Filters analytics by recipient email or phone number matching the user
 */
export async function fetchUserNotificationAnalytics(
  userId: string,
): Promise<NotificationAnalytic[]> {
  // First, get the user's email and phone to match against analytics
  const { data: userData, error: userError } = await supabaseClient
    .from("users")
    .select("email, phone")
    .eq("user_id", userId)
    .single();

  if (userError || !userData) {
    console.error("Error fetching user data for analytics:", userError);
    return [];
  }

  // Fetch analytics where the event contains notification data
  const { data, error } = await supabaseClient
    .from("analytics")
    .select("*")
    .in("class", ["LOG", "ERROR"])
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching notification analytics:", error);
    throw error;
  }

  // Filter analytics to only include those relevant to this user
  const userEmail = userData.email?.toLowerCase();
  const userPhone = userData.phone;

  const filteredData = data.filter((analytic) => {
    const event = analytic.event;

    // Check if this is a notification-related event using type guard
    if (!isNotificationEvent(event)) {
      return false;
    }

    // Match by email or phone
    const recipientEmail = event.recipient_email?.toLowerCase();
    const recipientPhone = event.recipient_phone;

    return (
      (userEmail && recipientEmail === userEmail) || (userPhone && recipientPhone === userPhone)
    );
  });

  return filteredData as NotificationAnalytic[];
}

/**
 * Hook to fetch and manage notification analytics for a specific user
 */
export function useUserNotificationAnalytics(userId: string) {
  const {
    data = [],
    isLoading,
    error,
    mutate,
  } = useSWR(userId ? ANALYTICS_CACHE_KEYS.USER_NOTIFICATIONS(userId) : null, () =>
    fetchUserNotificationAnalytics(userId),
  );

  return {
    analytics: data,
    isLoading,
    error,
    mutate,
  };
}
